import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { BookingModal } from "../components/meet-expert/BookingModal";
import { ProviderList } from "../components/meet-expert/ProviderList";
import { SessionGuard } from "../components/SessionGuard";
import { useAnalytics } from "../lib/analytics";
import { api } from "../lib/api";
import { API_CONFIG } from "../lib/constants/api";
import { useClientTranslations } from "../lib/i18n/client";
import styles from "./ConsultExpert.module.css";

interface Provider {
	providerUserId: string;
	providerName: string;
	businessName: string;
	logoUrl: string | null;
	relationshipStatus: string;
	connectedSince: string | null;
}

interface TransformedProvider {
	id: string;
	name: string;
	specialty: string;
	image?: string;
	isYourProvider?: boolean;
	rating?: number;
	nextAvailable?: string;
}

function ConsultExpertContent() {
	const { t } = useClientTranslations("consultExpert");
	const { logEvent } = useAnalytics();
	const { getAccessTokenSilently } = useAuth0();
	const [selectedProvider, setSelectedProvider] =
		useState<TransformedProvider | null>(null);
	const [showBookingModal, setShowBookingModal] = useState(false);
	const [authToken, setAuthToken] = useState<string>("");

	useEffect(() => {
		logEvent("consult_expert_page_viewed");
	}, [logEvent]);

	// Get auth token
	useEffect(() => {
		const fetchToken = async () => {
			try {
				const token = await getAccessTokenSilently();
				setAuthToken(token);
			} catch (error) {
				console.error("Error getting token:", error);
			}
		};
		fetchToken();
	}, [getAccessTokenSilently]);

	// Fetch user data to get userId
	const { data: userData } = useQuery({
		queryKey: ["user"],
		queryFn: async () => {
			const token = await getAccessTokenSilently();
			const response = await api.get("/api/user/by-email", {
				headers: { Authorization: `Bearer ${token}` },
			});
			return response.data;
		},
	});

	// Fetch providers
	const {
		data: providersData,
		isLoading,
		error: providersError,
	} = useQuery({
		queryKey: ["user-providers"],
		queryFn: async () => {
			const token = await getAccessTokenSilently();
			const response = await api.get("/api/user/providers", {
				headers: { Authorization: `Bearer ${token}` },
			});
			return response.data;
		},
		enabled: !!authToken,
	});

	const handleSelectProvider = (provider: TransformedProvider) => {
		setSelectedProvider(provider);
		setShowBookingModal(true);
		logEvent("meeting_provider_selected", undefined, {
			provider_id: provider.id,
			provider_name: provider.name,
			is_your_provider: provider.isYourProvider || false,
		});
	};

	const handleBookingComplete = () => {
		setShowBookingModal(false);
		setSelectedProvider(null);
	};

	// Transform providers data
	const providers: TransformedProvider[] =
		providersData?.providers?.map((p: Provider) => ({
			id: p.providerUserId,
			name: p.providerName,
			specialty: p.businessName,
			image: p.logoUrl || undefined,
			isYourProvider: true,
		})) || [];

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{/* Page Header */}
				<div className={styles.pageHeader}>
					<h1 className={styles.title}>{t("title")}</h1>
					<p className={styles.subtitle}>{t("subtitle")}</p>
				</div>

				{/* Main Content */}
				<div className={styles.content}>
					{isLoading ? (
						<div className={styles.loading}>
							<div className={styles.spinner} />
						</div>
					) : providersError ? (
						<div className={styles.error}>
							<p>{t("errorLoading")}</p>
							<p className={styles.errorDetail}>
								{providersError instanceof Error
									? providersError.message
									: "Unknown error"}
							</p>
						</div>
					) : (
						<>
							{/* Your Provider Section */}
							{providers.length > 0 ? (
								<div className={styles.section}>
									<h2 className={styles.sectionTitle}>
										<span className={styles.icon}>👥</span>
										{t("sections.yourProvider")}
									</h2>
									<ProviderList
										providers={providers}
										onSelectProvider={handleSelectProvider}
									/>
								</div>
							) : (
								<div className={styles.section}>
									<h2 className={styles.sectionTitle}>
										<span className={styles.icon}>👥</span>
										{t("sections.yourProvider")}
									</h2>
									<div className={styles.emptyState}>
										<span className={styles.emptyIcon}>🔍</span>
										<p className={styles.emptyText}>{t("emptyState")}</p>
									</div>
								</div>
							)}

							{/* Other Providers Section */}
							<div className={styles.section}>
								<h2 className={styles.sectionTitle}>
									<span className={styles.icon}>👥</span>
									{t("sections.otherProviders")}
								</h2>
								<div className={styles.comingSoon}>
									<span className={styles.comingSoonIcon}>👥</span>
									<h3 className={styles.comingSoonTitle}>
										{t("comingSoon.title")}
									</h3>
									<p className={styles.comingSoonText}>
										{t("comingSoon.description")}
									</p>
								</div>
							</div>
						</>
					)}
				</div>
			</main>

			{/* Booking Modal */}
			{showBookingModal && selectedProvider && userData?.user && (
				<BookingModal
					provider={selectedProvider}
					onClose={() => {
						setShowBookingModal(false);
						setSelectedProvider(null);
					}}
					onBook={handleBookingComplete}
					userId={userData.user.id}
					apiBaseUrl={API_CONFIG.BASE_URL}
					authToken={authToken}
				/>
			)}
		</div>
	);
}

export function ConsultExpert() {
	return (
		<SessionGuard>
			<ConsultExpertContent />
		</SessionGuard>
	);
}
